import { useUser } from "@saleor/auth";
import { useUserPermissions } from "@saleor/auth/hooks/useUserPermissions";
import { Zone } from "@saleor/auth/types";
import { changeRole } from "@saleor/components/AppLayout";
import Container from "@saleor/components/Container";
import PageHeader from "@saleor/components/PageHeader";
import { defaultListSettings } from "@saleor/config";
import {
  getNotRefDraftOrderPath,
  orderCartPath
} from "@saleor/createOrder/urls";
import {
  OrderType,
  PermissionEnum,
  useOrderDraftsQuery
} from "@saleor/graphql";
import useListSettings from "@saleor/hooks/useListSettings";
import useLocalPaginator, {
  useLocalPaginationState
} from "@saleor/hooks/useLocalPaginator";
import useNavigator from "@saleor/hooks/useNavigator";
import { sectionNames } from "@saleor/intl";
// import { makeStyles } from "@saleor/macaw-ui";
import { capitalize } from "@saleor/misc";
import { ListViews } from "@saleor/types";
import { mapEdgesToItems } from "@saleor/utils/maps";
import { getSortParams, getSortUrlVariables } from "@saleor/utils/sort";
import React, { FC, useEffect, useMemo } from "react";
import { useIntl } from "react-intl";

import OrderDraftsTable from "../components/OrderDraftsTable";
import { SaveDraftListQueryParam, saveDraftListUrl } from "../urls";
import { getSortOrderDraftFilterTable } from "./sort";

// const useStyles = makeStyles(
//   theme => ({
//     inlineBlock: {
//       display: "inline-block",
//       color: theme.palette.grey[900]
//     }
//   }),
//   { name: "SaveDraftView" }
// );

interface SaveDraftViewProps {
  params: SaveDraftListQueryParam;
}
const SaveDraftView: FC<SaveDraftViewProps> = ({ params }) => {
  const intl = useIntl();
  const navigate = useNavigator();
  // const classes = useStyles();

  const { updateListSettings, settings } = useListSettings(
    ListViews.FILTER_ORDER
  );

  const [
    valuesPaginationState,
    setValuesPaginationState
  ] = useLocalPaginationState(settings?.rowNumber);
  const paginateValues = useLocalPaginator(setValuesPaginationState);

  const queryVariables = React.useMemo(
    () => ({
      ...valuesPaginationState,
      sortBy: getSortOrderDraftFilterTable(params)
    }),
    [settings.rowNumber, valuesPaginationState, params]
  );

  const permissions = useUserPermissions();
  const extensionsPermissionExport = permissions?.find(
    perm => perm.code === PermissionEnum.VIEW_ORDER_EXPORT
  );
  const extensionsPermissionDomestic = permissions?.find(
    perm => perm.code === PermissionEnum.VIEW_ORDER_DOMESTIC
  );
  const { zone } = useUser();

  const getTypeOrder = () => {
    if (
      (extensionsPermissionExport && !extensionsPermissionDomestic) ||
      zone === Zone.Export
    ) {
      return OrderType.EXPORT;
    } else if (extensionsPermissionDomestic) {
      return OrderType.DOMESTIC;
    } else {
      return OrderType.CUSTOMER;
    }
  };

  const {
    data: dataOrderDraft,
    loading: loadingDataOrderDraft
  } = useOrderDraftsQuery({
    displayLoader: true,
    variables: {
      ...queryVariables,
      filter: {
        type: getTypeOrder()
      }
    }
  });

  const { loadNextPage, loadPreviousPage, pageInfo } = paginateValues(
    dataOrderDraft?.orderDrafts?.pageInfo,
    valuesPaginationState
  );

  useEffect(() => {
    updateListSettings("rowNumber", defaultListSettings.FILTER_ORDER.rowNumber);
  }, []);

  const resetPaginationState = () => {
    setValuesPaginationState({
      first: settings?.rowNumber,
      after: undefined,
      before: undefined
    });
  };

  const lastPaginationState = () => {
    setValuesPaginationState({
      last: dataOrderDraft?.orderDrafts?.latestPageItemNumber
    });
  };

  const handleNextLastPage = () => {
    lastPaginationState();
  };

  const handlePreviousFirstPage = () => {
    resetPaginationState();
  };

  const orderDrafts = useMemo(
    () => mapEdgesToItems(dataOrderDraft?.orderDrafts),
    [dataOrderDraft]
  );

  const handleSort = field => {
    resetPaginationState();
    navigate(
      saveDraftListUrl({
        ...params,
        ...getSortUrlVariables(field, params)
      }),
      { replace: true }
    );
  };

  const handleRowClick = (id: string, zone: string) => {
    const selectedDraft = orderDrafts.find(draft => draft.id === id);

    if (selectedDraft) {
      const { contractPiNo } = selectedDraft;

      if (capitalize(zone) === Zone.Export) {
        changeRole.current.setRole(Zone.Export);
      }
      if (capitalize(zone) === Zone.Domestic) {
        changeRole.current.setRole(Zone.Domestic);
      }

      sessionStorage.setItem("previousRole", capitalize(zone));
      setTimeout(() => {
        if (contractPiNo === "") {
          navigate(getNotRefDraftOrderPath(id));
        } else {
          navigate(orderCartPath(id));
        }
      }, 300);
      return;
    }
  };

  useEffect(() => {
    resetPaginationState();
  }, [settings?.rowNumber]);

  return (
    <Container>
      <PageHeader title={intl.formatMessage(sectionNames.saveDraft)} />

      <OrderDraftsTable
        data={orderDrafts}
        disabled={loadingDataOrderDraft}
        pageInfo={pageInfo}
        onNextPage={loadNextPage}
        onPreviousPage={loadPreviousPage}
        onUpdateListSettings={updateListSettings}
        onRowClick={handleRowClick}
        sort={getSortParams(params)}
        onSort={handleSort}
        settings={settings}
        onPreviousFirstPage={handlePreviousFirstPage}
        onNextLastPage={handleNextLastPage}
        totalCount={dataOrderDraft?.orderDrafts?.totalCount}
        latestPageItemNumber={dataOrderDraft?.orderDrafts?.latestPageItemNumber}
      />
    </Container>
  );
};

export default SaveDraftView;
